import { useRef, useState } from 'react';
import type { ListFilters } from '../api';
import { ar } from '../ar';
import { IconCalendar, IconClose, IconFilter, IconSearch } from './icons';
import { Popover, Sheet, useIsDesktop } from './overlays';
import { Button } from './primitives';

/**
 * Filtering.
 *
 * The rule the old build broke: filters must not consume half the page. So the always-
 * visible surface is one compact row — date, room, and a single "filters" control — and
 * everything else lives in a floating panel that is a popover on a pointer and a bottom
 * sheet under a thumb. Whatever is active comes back as removable chips, so the current
 * query is legible without opening anything.
 */

export interface SupervisorOption {
  id: string;
  name: string;
}

/** One active filter, rendered as a removable chip. */
interface ActiveFilter {
  key: keyof ListFilters;
  label: string;
  value: string;
}

function describeActive(
  filters: ListFilters,
  supervisors: SupervisorOption[],
  defaults: ListFilters,
): ActiveFilter[] {
  const active: ActiveFilter[] = [];

  if (filters.from && filters.from !== defaults.from) {
    active.push({ key: 'from', label: ar.dateFrom, value: filters.from });
  }
  if (filters.to && filters.to !== defaults.to) {
    active.push({ key: 'to', label: ar.dateTo, value: filters.to });
  }
  if (filters.room) active.push({ key: 'room', label: ar.roomNumber, value: filters.room });
  if (filters.type) {
    active.push({
      key: 'type',
      label: ar.inspectionType,
      value: filters.type === 'CHECKOUT' ? ar.checkout : ar.clean,
    });
  }
  if (filters.supervisorId) {
    const name = supervisors.find((s) => s.id === filters.supervisorId)?.name;
    active.push({ key: 'supervisorId', label: ar.supervisor, value: name ?? '—' });
  }
  if (filters.missingItems) {
    active.push({
      key: 'missingItems',
      label: ar.missingItemsFilter,
      value: filters.missingItems === 'yes' ? ar.hasMissing : ar.noMissing,
    });
  }
  if (filters.edited) {
    active.push({
      key: 'edited',
      label: ar.editedFilter,
      value: filters.edited === 'yes' ? ar.editedOnly : ar.notEdited,
    });
  }
  return active;
}

export function FilterBar({
  filters,
  defaults,
  supervisors,
  onChange,
  onClear,
  showDateRange = true,
}: {
  filters: ListFilters;
  defaults: ListFilters;
  supervisors: SupervisorOption[];
  onChange: (next: ListFilters) => void;
  onClear: () => void;
  showDateRange?: boolean;
}) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState<ListFilters>(filters);
  const isDesktop = useIsDesktop(1024);
  const anchorRef = useRef<HTMLDivElement>(null);

  const active = describeActive(filters, supervisors, defaults);

  const openPanel = () => {
    setDraft(filters);
    setPanelOpen(true);
  };

  const apply = () => {
    onChange(draft);
    setPanelOpen(false);
  };

  const removeFilter = (key: keyof ListFilters) => {
    const next: ListFilters = { ...filters };
    if (key === 'from') next.from = defaults.from;
    else if (key === 'to') next.to = defaults.to;
    else next[key] = '' as never;
    onChange(next);
  };

  const advanced = (
    <div className="filter-form">
      {showDateRange && (
        <div className="filter-row">
          <label className="field">
            <span className="field-label">{ar.dateFrom}</span>
            <input
              className="input"
              type="date"
              value={draft.from ?? ''}
              onChange={(event) => setDraft({ ...draft, from: event.target.value })}
              dir="ltr"
            />
          </label>
          <label className="field">
            <span className="field-label">{ar.dateTo}</span>
            <input
              className="input"
              type="date"
              value={draft.to ?? ''}
              onChange={(event) => setDraft({ ...draft, to: event.target.value })}
              dir="ltr"
            />
          </label>
        </div>
      )}

      <div className="filter-row">
        <label className="field">
          <span className="field-label">{ar.inspectionType}</span>
          <select
            className="input"
            value={draft.type ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, type: event.target.value as ListFilters['type'] })
            }
          >
            <option value="">{ar.allTypes}</option>
            <option value="CHECKOUT">{ar.checkout}</option>
            <option value="CLEAN_ROOM">{ar.clean}</option>
          </select>
        </label>

        <label className="field">
          <span className="field-label">{ar.supervisor}</span>
          <select
            className="input"
            value={draft.supervisorId ?? ''}
            onChange={(event) => setDraft({ ...draft, supervisorId: event.target.value })}
          >
            <option value="">{ar.allSupervisors}</option>
            {supervisors.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="filter-row">
        <label className="field">
          <span className="field-label">{ar.missingItemsFilter}</span>
          <select
            className="input"
            value={draft.missingItems ?? ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                missingItems: event.target.value as ListFilters['missingItems'],
              })
            }
          >
            <option value="">{ar.any}</option>
            <option value="yes">{ar.hasMissing}</option>
            <option value="no">{ar.noMissing}</option>
          </select>
        </label>

        <label className="field">
          <span className="field-label">{ar.editedFilter}</span>
          <select
            className="input"
            value={draft.edited ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, edited: event.target.value as ListFilters['edited'] })
            }
          >
            <option value="">{ar.any}</option>
            <option value="yes">{ar.editedOnly}</option>
            <option value="no">{ar.notEdited}</option>
          </select>
        </label>
      </div>
    </div>
  );

  const panelFooter = (
    <>
      <Button variant="accent" onClick={apply} block>
        {ar.applyFilters}
      </Button>
      <Button variant="ghost" onClick={() => setPanelOpen(false)}>
        {ar.cancel}
      </Button>
    </>
  );

  return (
    <div className="filter-bar" ref={anchorRef}>
      <div className="filter-primary">
        {showDateRange ? (
          <label className="filter-date">
            <IconCalendar size={15} />
            <input
              className="filter-date-input num"
              type="date"
              value={filters.from ?? ''}
              onChange={(event) => onChange({ ...filters, from: event.target.value, to: event.target.value })}
              dir="ltr"
              aria-label={ar.date}
            />
          </label>
        ) : null}

        <label className="filter-search">
          <IconSearch size={15} />
          <input
            className="filter-search-input num"
            value={filters.room ?? ''}
            onChange={(event) =>
              onChange({ ...filters, room: event.target.value.replace(/\D/g, '').slice(0, 4) })
            }
            placeholder={ar.roomNumberPlaceholder}
            inputMode="numeric"
            dir="ltr"
            aria-label={ar.roomNumber}
          />
        </label>

        <div className="filter-more">
          <Button
            variant="outline"
            size="sm"
            onClick={openPanel}
            icon={<IconFilter size={14} />}
            aria-expanded={panelOpen}
          >
            {ar.filters}
            {active.length > 0 && <span className="filter-count num">{active.length}</span>}
          </Button>

          {isDesktop && (
            <Popover open={panelOpen} onClose={() => setPanelOpen(false)}>
              <p className="popover-title">{ar.filters}</p>
              {advanced}
              <div className="popover-foot">{panelFooter}</div>
            </Popover>
          )}
        </div>

        {active.length > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            {ar.clearFilters}
          </Button>
        )}
      </div>

      {active.length > 0 && (
        <ul className="filter-chips" aria-label={ar.activeFilters}>
          {active.map((item) => (
            <li key={item.key}>
              <span className="filter-chip">
                <span className="filter-chip-label">{item.label}</span>
                <span className="filter-chip-value num">{item.value}</span>
                <button
                  className="filter-chip-remove"
                  onClick={() => removeFilter(item.key)}
                  aria-label={`${ar.removeFilter} ${item.label}`}
                >
                  <IconClose size={12} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {!isDesktop && (
        <Sheet
          open={panelOpen}
          title={ar.filters}
          onClose={() => setPanelOpen(false)}
          footer={panelFooter}
        >
          {advanced}
        </Sheet>
      )}
    </div>
  );
}
